import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import ICustomer from '../_interfaces/ICustomer';
import ISite from '../_interfaces/ISite';
import { SessionService } from '../_services/session/session.service';

type RouteAccessData = {
  requiredModule?: string | string[];
  requiredPermissionsAny?: string[];
  requiredPermissionsAll?: string[];
};

@Injectable({
  providedIn: 'root',
})
export class WorkspaceAccessGuard implements CanActivate {
  constructor(
    private readonly session: SessionService,
    private readonly router: Router,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | UrlTree {
    const data = (route.data || {}) as RouteAccessData;
    const customer = this.resolveCustomer(route);

    if (!customer) {
      return this.router.createUrlTree(['/entry']);
    }

    const requiredModules = Array.isArray(data.requiredModule)
      ? data.requiredModule
      : data.requiredModule
        ? [data.requiredModule]
        : [];

    if (
      requiredModules.length &&
      !requiredModules.every((identifier) =>
        this.isCommercialModuleEnabled(customer, identifier),
      )
    ) {
      return this.buildFallbackUrl(state.url, customer);
    }

    const requiredAny = [
      ...new Set((data.requiredPermissionsAny || []).filter(Boolean)),
    ];
    const requiredAll = [
      ...new Set((data.requiredPermissionsAll || []).filter(Boolean)),
    ];

    if (!requiredAny.length && !requiredAll.length) {
      return true;
    }

    const site = this.resolveSite(route, customer);
    if (!site) {
      return this.buildFallbackUrl(state.url, customer);
    }

    if (requiredAny.length) {
      const hasAny = requiredAny.some((permission) =>
        this.session.hasPermission(customer, site, permission),
      );
      if (!hasAny) {
        return this.buildFallbackUrl(state.url, customer);
      }
    }

    if (requiredAll.length) {
      const hasAll = requiredAll.every((permission) =>
        this.session.hasPermission(customer, site, permission),
      );
      if (!hasAll) {
        return this.buildFallbackUrl(state.url, customer);
      }
    }

    return true;
  }

  private readParam(route: ActivatedRouteSnapshot, key: string): string {
    for (const snapshot of [...route.pathFromRoot].reverse()) {
      const value = `${snapshot.params?.[key] || ''}`.trim();
      if (value) {
        return value;
      }
    }

    return '';
  }

  private resolveCustomer(route: ActivatedRouteSnapshot): ICustomer | null {
    const rawCustomer =
      this.readParam(route, 'customer') ||
      this.readParam(route, 'customers_id');
    const permissions = this.session.getPermissions() || [];

    const matched = permissions.find(
      (customer) =>
        `${customer?.identifier || ''}` === rawCustomer ||
        `${customer?.id || ''}` === rawCustomer,
    );

    if (matched) {
      return matched;
    }

    const sessionCustomer = this.session.getCustomer();
    if (
      sessionCustomer &&
      (`${sessionCustomer.identifier || ''}` === rawCustomer ||
        `${sessionCustomer.id || ''}` === rawCustomer)
    ) {
      return sessionCustomer;
    }

    return null;
  }

  private resolveSite(
    route: ActivatedRouteSnapshot,
    customer: ICustomer,
  ): ISite | null {
    const rawSite =
      this.readParam(route, 'site') || this.readParam(route, 'sites_id');

    if (!rawSite) {
      return null;
    }

    const matched = (customer?.sites || []).find(
      (site) =>
        `${site?.identifier || ''}` === rawSite ||
        `${site?.id || ''}` === rawSite,
    );

    if (matched) {
      return matched;
    }

    const sessionSite = this.session.getSite();
    if (
      sessionSite &&
      (`${sessionSite.identifier || ''}` === rawSite ||
        `${sessionSite.id || ''}` === rawSite)
    ) {
      return sessionSite;
    }

    return null;
  }

  private normalizeCommercialModuleIdentifier(identifier: string): string {
    const raw = `${identifier || ''}`.trim().toLowerCase();
    return raw === 'statistics' ? 'analytics' : raw;
  }

  private isCommercialModuleEnabled(
    customer: ICustomer,
    identifier: string,
  ): boolean {
    const normalized = this.normalizeCommercialModuleIdentifier(identifier);
    if (normalized === 'appointments' || normalized === 'patients') {
      return true;
    }

    const modules = customer?.commercialModules as any;
    if (!modules) {
      return normalized !== 'incidents';
    }

    return modules?.[normalized] !== false;
  }

  private buildFallbackUrl(currentUrl: string, customer: ICustomer): UrlTree {
    if (currentUrl.startsWith('/administration/')) {
      return this.router.createUrlTree([
        '/administration',
        customer?.identifier || customer?.id || '',
      ]);
    }

    return this.router.createUrlTree(['/entry']);
  }
}
